import { Modal } from '../../components/modal/modal';
import {
  GeneralLedgerAccount,
  GeneralLedgerAccountRequest,
  GeneralLedgerAccountType,
} from '../../clients/quipli-service/accounting/accounting';
import React, { useContext, useEffect, useState } from 'react';
import { Button } from '../../components/form/button';
import { useAccountingApiV1 } from '../../clients/quipli-service/accounting/accounting-api-v1';
import { toast } from '../../util/toasts';
import { SelectForm } from '../../components/select-form/select-form';
import { QBOAccount } from '../../clients/quipli-service/qbo/qbo';
import { useQboApi } from '../../clients/quipli-service/qbo/qbo-api-v1';
import { TenantContext } from '../../context/tenant-context';
import { QboAccountSelect } from '../qbo/qbo-account-select';
import { Spinner } from '../../components/spinner/spinner';

export const AccountModal = ({
  account,
  isOpen,
  close,
}: {
  account?: GeneralLedgerAccount;
  isOpen: boolean;
  close: (isWrite: boolean) => void;
}) => {
  const { createAccount, updateAccount } = useAccountingApiV1();
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [accountRequest, setAccountRequest] = useState<GeneralLedgerAccountRequest>({
    name: '',
    number: '',
    accountType: GeneralLedgerAccountType.Income,
  });
  const [qboAccounts, setQboAccounts] = useState<QBOAccount[] | undefined>();
  const { configuration } = useContext(TenantContext);
  const { listAccounts } = useQboApi();
  let qboEnabled = false;

  // @hipaa-exclude-start
  if (configuration?.qboEnabled) {
    qboEnabled = true;
  }

  useEffect(() => {
    if (qboEnabled) {
      listAccounts()
        .then((accounts) => {
          setQboAccounts(accounts);
        })
        .catch((e) => {
          console.error(`Error loading QuickBooks accounts`, e);
          toast(`Error loading QuickBooks accounts`, 'error');
        });
    }
  }, [configuration?.qboEnabled]);
  // @hipaa-exclude-end

  useEffect(() => {
    account && setAccountRequest(account);
  }, [account]);

  async function doSave() {
    try {
      setIsSaving(true);
      if (account) {
        await updateAccount(account.id, accountRequest);
      } else {
        await createAccount(accountRequest);
      }
      toast(account ? 'Account updated successfully' : 'Account created successfully');
      close(true);
    } catch (e: any) {
      if (e.status === 400) {
        toast(e.message, 'error');
      } else {
        toast('Failed to save account', 'error');
      }
    } finally {
      setIsSaving(false);
    }
  }

  const accountTypeOptions = [
    { label: 'Tax', value: GeneralLedgerAccountType.Tax },
    { label: 'Income', value: GeneralLedgerAccountType.Income },
    { label: 'Expense', value: GeneralLedgerAccountType.Expense },
  ];

  return (
    <Modal
      title={`${account ? 'Update' : 'Create'} Account`}
      isOpen={isOpen}
      close={() => close(false)}
      actions={
        <>
          <Button className="button button-secondary" disabled={isSaving} onClick={() => close(false)}>
            Cancel
          </Button>
          <Button className="button button-yellow" disabled={isSaving} onClick={doSave}>
            {account ? 'Update' : 'Create'}
          </Button>
        </>
      }
    >
      <div className="flex flex-column gap-2">
        <div className="flex items-center gap-2">
          <label className="basis-1/2">Account Number</label>
          <input
            className="basis-1/2"
            type="text"
            value={accountRequest.number}
            onChange={(e) =>
              setAccountRequest((prev) => {
                return { ...prev, number: e.target.value };
              })
            }
          />
        </div>
        <div className="flex items-center gap-2">
          <label className="basis-1/2">Account Name</label>
          <input
            className="basis-1/2"
            type="text"
            value={accountRequest.name}
            onChange={(e) =>
              setAccountRequest((prev) => {
                return { ...prev, name: e.target.value };
              })
            }
          />
        </div>
        <div className="flex items-center gap-2">
          <label className="basis-1/2">Account Type</label>
          <SelectForm
            menuShouldBlockScroll
            menuPosition="fixed"
            menuPortalTarget={document.body}
            className="basis-1/2"
            value={accountTypeOptions.find((opt) => opt.value === accountRequest.accountType)}
            onChange={(e) => {
              setAccountRequest((prev) => {
                return { ...prev, accountType: e!.value };
              });
            }}
            isRequired={true}
            options={accountTypeOptions}
          />
        </div>
        {qboEnabled && accountRequest.accountType !== GeneralLedgerAccountType.Tax && (
          <div className="flex items-center gap-2">
            <label className="basis-1/2">QBO Account Mapping</label>
            {qboAccounts ? (
              <QboAccountSelect
                accounts={qboAccounts.filter((a) => {
                  if (accountRequest.accountType === GeneralLedgerAccountType.Income) {
                    return a.Classification === 'Revenue';
                  } else if (accountRequest.accountType === GeneralLedgerAccountType.Expense) {
                    return a.Classification === 'Expense';
                  } else {
                    return false;
                  }
                })}
                onChange={(v) =>
                  setAccountRequest((prev) => {
                    return { ...prev, qboAccountId: v };
                  })
                }
                accountId={accountRequest.qboAccountId}
              />
            ) : (
              <Spinner />
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};
